import {Injectable} from '@angular/core';
import {filter, map} from 'rxjs/operators';
import {combineLatest} from 'rxjs';

import {AngularFireAuth} from '@angular/fire/compat/auth';
import {Client, User, Address} from '@nxt/model-core';

import {FireService} from './fire.service';
import {ClientService} from '../../shared/_services/client.service';
import {LocalStorageService} from '../../shared/_services/local-storage.service';
import {UserService} from './user.service';

import {MessagingService} from './messaging.service';

// @ts-ignore
import {environment} from '@env/environment';

@Injectable()
export class AccountService {
    uBR: any = {};

    constructor(
        private cSvc: ClientService,
        private uSvc: UserService,
        private mSvc: MessagingService,
        private fSvc: FireService,
        private lSvc: LocalStorageService,
        public auth: AngularFireAuth
    ) {

        this.cSvc.signedOut = this.auth.authState.pipe(filter(u => !u));

        combineLatest([this.uSvc.user$, this.cSvc.clients$, this.cSvc.c$])
            .pipe(map(res => {
                return {user: res[0], clients: res[1], c: res[2]};
            }))
            .subscribe(
                async (result) => {

                    if (result.user?.id) {
                        if (result.clients?.length
                            && result.c?.id
                        ) {

                            let client: Client = result.clients.find(item => item.id === result.c.id);
                            if (!client) {
                                client = result.clients[0];
                            }
                            await this.setClient(client);

                            // Make sure active thread doesn't stay alive when jumping between clients.
                            this.mSvc.thread$.next(null);
                            this.uBR = {};

                            // let devices = await this.fSvc.getColl(`users/${result.user.id}/devices`).toPromise();
                            // Load the user's location with respect to the current client.
                            let doc = await this.fSvc.getDoc(`users/${result.user.id}/locations/${this.cSvc.client_id}`).toPromise();
                            if (doc?.exists && doc?.data()?.ref) {
                                let a: Address = new Address(await doc.data().ref.get())
                                this.uSvc.location$.next(a);
                            } else {
                                this.uSvc.location$.next(null);
                            }

                            // load user signature
                            doc = await this.fSvc.getDoc(`users/${result.user.id}/signatures/${this.cSvc.client_id}`).toPromise();
                            if (doc?.exists && doc?.data()?.signature) {
                                this.uSvc.signature$.next(doc?.data()?.signature);
                            } else {
                                this.uSvc.signature$.next('');
                            }

                            await this.getUsersByRole();
                        }
                    } else if (result.user !== undefined) {
                        await this.setClient(null);
                        this.uBR = {};
                        await this.lSvc.flushState();
                    }
                }
            );

        this.auth.user.subscribe(
            async (user: any) => {
                if (user) {
                    return this.loadClients(user);
                } else {
                    this.cSvc.u$.next(null);
                    this.uSvc.user$.next(null);
                    await this.setClient(null);
                    this.cSvc.clients$.next(null);
                }
            },
            async (err: any) => {
                console.warn(err);
                await this.signOut();
            }
        );

    }

    async loadClients(user) {
        try {
            user = user?.multiFactor?.user || user;
            let u: User =  ( await this.fSvc.getObject(`users/${user.uid}`) ) as User;
            if (u?._exists) {
                let result: { terms: any, roles: any, clients: any[] } = await this.cSvc.callAPI(`/cms/user/roles`, 'get', null, await user.getIdToken());

                let clients: Client[] = [];

                if (Object.keys(result?.roles)?.length) {
                    if (result.roles[environment.default_client.id]) {
                        let q = await u._docRef.firestore.collection('clients').where('active','==',true).orderBy('name','asc').get();
                        clients = q.docs.map(d => new Client(d)) || [];
                    } else {
                        await Promise.all(Object.keys(result.roles).map(async id => {
                            try {
                                let c: Client = new Client( await u._docRef.firestore.doc(`clients/${id}`).get() );
                                clients.push(c);
                            } catch (e) {
                                console.warn(e);
                            }
                        }));
                        console.log(clients);
                        clients.sort((a, b) => (a.name > b.name) ? 1 : -1);
                        console.log('clients', clients);
                    }
                    this.uSvc.roles$.next(result.roles || {});
                    this.cSvc.clients$.next(clients || []);
                } else {
                    this.uSvc.roles$.next(null);
                    this.cSvc.clients$.next(null);
                }
                if (result?.terms) {
                    this.cSvc.terms$.next(result.terms);
                }
                if (user?.uid !== this.cSvc.u$.getValue()?.uid) {
                    this.cSvc.u$.next(user);
                }

                if (clients?.length) {
                    if (this.uSvc.user$.getValue()?.id !== u.id) {
                        this.uSvc.user$.next(u);
                        this.watchUser(u);
                    }
                } else {
                    this.uSvc.user$.next(null);
                }
            } else {
                this.uSvc.user$.next(null);
                this.uSvc.roles$.next(null);
                this.cSvc.clients$.next(null);
            }
        } catch (e) {
            console.log(e);
            if (e?.error?.title === 'Permission Denied') {
                await this.signOut();
            } else {
                console.warn(e, 'user');
            }
        }
    }

    async watchUser(u:any) {
        this.fSvc.watchObject(`users/${u.id}`, null, true)
            .subscribe({
                next: ([obj,delta]) => {
                    this.mSvc.counts$.next(obj['unread']);
                },
                error: (e:any) => {
                    console.warn(e, 'user2');
                }
            });
    }

    async loadImages(client: Client) {
        this.uSvc.images = this.uSvc.images || {};
        this.uSvc.images[client.id] = {};
    }

    async signOut() {
        await this.lSvc.flushState();
        await this.auth.signOut();
    }

    async getUsersByRole(): Promise<any> {
        this.uBR = this.uBR || {};
        if (this.cSvc.client$.getValue()?.name_key) {
            if (!this.uBR[this.cSvc.client$.getValue().name_key]) {

                let uBR: any = await this.cSvc.callAPI(`/cms/users_by_role`, 'get');
                if (
                    this.uSvc.isRole(['restricted'])
                    && !this.uSvc.isRole(['admin'])
                ) {
                    Object.keys(uBR).map(key => {
                        uBR[key] = uBR[key]?.reduce((users,u) => {
                            if (!u._restricted || u.id === this.uSvc.user$.getValue().id) {
                                users.push(u);
                            }
                            return users;
                        }, []);
                    });
                }
                uBR.agents = this.cSvc.client$.getValue()?.agents;
                this.uBR[this.cSvc.client$.getValue().name_key] = uBR;
            }
            return this.uBR[this.cSvc.client$.getValue().name_key];
        }
    }

    async setClient(client: Client) {
        // Load users claims for this client
        this.mSvc.thread$.next(null);
        this.cSvc.client$.next(client);
    }
}
